/**
 * This component will be used to bookmark the product
 *
 * @module views/components/ProductBookmark
 * @memberof -Common
 */
import './ProductBookmark.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { getStorage, removeStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import * as utils from './ProductBookmark';

/**
 * Represents a ProductBookmark component
 *
 * @method
 * @param {ProductBookmarkProps} props - React properties passed from composition
 * @returns ProductBookmark
 */
const ProductBookmark = function( props ){
  const { user } = useUserContext();
  const hasCheckedPostLoginStatus = useRef( false );

  // Stop gap for ATG header functionality, we need to save if this was true and wait for the
  // full page refresh where it will then be reset
  const isAtgRefreshing = useRef( false );
  const wasDSOTFLoggedOut = useRef( false );

  const { skuId, loading, bookmarked, invokeMutation, removeBookmarkAction, bookmarkAction } = props;
  const action = bookmarked ? removeBookmarkAction : bookmarkAction;

  const dispatchLogin =
    action?.clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNIN && !( user.loginStatus );

  const data = {
    wasDSOTFLoggedOut,
    isAtgRefreshing,
    skuId,
    action,
    dispatchLogin,
    variables: {
      contentId: props.id
    }
  };
  const methods = {
    invokeMutation
  };

  /*
   * Toggle favorite mutation dispatcher
   */
  const toggleFavorite = useCallback( utils.composeToggleFavorite( data, methods ), [
    dispatchLogin,
    action,
    bookmarked,
    skuId
  ] );

  /*
   * Listens for post login response
   */
  const postLoginFavoriteHandler = useCallback(
    utils.composePostLoginFavorite(
      {
        loading,
        bookmarked,
        dispatchLogin,
        isAtgRefreshing,
        hasCheckedPostLoginStatus,
        skuId,
        action
      },
      { toggleFavorite }
    ),
    [loading, bookmarked, dispatchLogin, isAtgRefreshing, hasCheckedPostLoginStatus, skuId, toggleFavorite]
  );

  useEffect( postLoginFavoriteHandler, [postLoginFavoriteHandler] );

  return (
    <Button
      secondary
      className='ProductBookmark'
      iconSize='m'
      onClick={ toggleFavorite }
      iconImage={ bookmarked ? 'HeartFilled' : 'HeartEmpty' }
      ariaLabel={ action?.label }
      action={ action }
      crossButtonVisibility={ true }
    />
  );
};

/**
 * @method composePostLoginFavorite
 * @param {object} data - Data arguments
 * @param {string} data.skuId - skuId
 * @param {boolean} data.dispatchLogin - If the action is for opening a sign in overlay
 * @param {boolean} data.bookmarked - If the product has been bookmarked or not
 * @param {boolean} data.loading - Loading set to true or false
 * @param {boolean} data.hasCheckedPostLoginStatus - Sets to true or false
 * @param {object} data.user - User
 * @param {object} methods - Methods
 * @param {function} methods.toggleFavorite - Handles toggleFavorite methods
 */
export const composePostLoginFavorite = ( data, methods ) => () => {
  const {
    bookmarked,
    loading,
    hasCheckedPostLoginStatus = {},
    skuId,
    isAtgRefreshing = {},
    dispatchLogin,
    action
  } = handleEmptyObjects( data );

  if( !action?.graphql ){
    return;
  }
  const { toggleFavorite } = methods || {};

  if(
    loading ||
    bookmarked ||
    dispatchLogin ||
    isAtgRefreshing.current || // TODO: When DSOTF header goes live this will be removed
    hasCheckedPostLoginStatus?.current
  ){
    return;
  }
  // hasCheckedPostLoginStatus.current = true;

  // Always set to true, regardless of ATG/DSOTF status
  hasCheckedPostLoginStatus.current = true;

  const postLoginSku = getStorage( { secure: true, key: STORAGE_KEY.postLoginFavorite } );
  removeStorage( { secure: true, key: STORAGE_KEY.postLoginFavorite } );

  if( postLoginSku?.skuId !== skuId ){
    return;
  }

  toggleFavorite();
};

/**
 * @method composeToggleFavorite
 * @param {object} data - Data arguments
 * @param {boolean} data.dispatchLogin - Is this action a sign in action
 * @param {object} data.variables - GraphQL Variables
 * @param {object} data.isAtgRefreshing - Ref that contains ATG logged out status
 * @param {object} data.wasDSOTFLoggedOut - Ref that contains ATG logged out status
 * @param {object} data.action - Action contains graphql/customHeaders/param
 * @param {object} data.skuId - SkuId
 * @param {object} methods - Methods
 * @param {function} methods.invokeMutation - Handles invokeMutation
 */
export const composeToggleFavorite = ( data, methods ) => () => {
  const { dispatchLogin, isAtgRefreshing = {}, wasDSOTFLoggedOut = {}, skuId, variables, action } = data || {};
  const { invokeMutation } = methods || {};
  const { graphql, customHeaders, params } = action || {};

  if( dispatchLogin ){
    // Save if we're in an ATG experience and the previous state was logged out
    // this will prevent the post login favorite from trying to favorite before the refresh
    // TODO: When DSOTF header goes live this will be removed
    isAtgRefreshing.current = action?.clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNIN;
    wasDSOTFLoggedOut.current = !isAtgRefreshing.current;
    setStorage( { secure: true, key: STORAGE_KEY.postLoginFavorite, value: { skuId } } );
    return;
  }

  if( !graphql || !invokeMutation ){
    return;
  }

  const mutation = {
    graphql,
    customHeaders,
    params,
    variables
  };

  invokeMutation( mutation );
};

/**
 * Property type definitions
 *
 * @typedef ProductBookmarkProps
 * @type {object}
 * @property {boolean} bookmarked - Flag to render the Heart icon state
 * @property {object.<{label: string, graphql: string}>} bookmarkAction - Sets the bookmarkAction properties
 * @property {object.<{label: string, graphql: string}>} removeBookmarkAction - Sets the removeBookmarkAction properties
 */
export const propTypes = {
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string,
    clientActionType: PropTypes.oneOfType( [PropTypes.object, PropTypes.string] ),
    navigationType: PropTypes.oneOfType( [PropTypes.object, PropTypes.string] )
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} bookmarked=false - Defaults bookmarked to false
 */
export const defaultProps = {
  bookmarked: false
};

ProductBookmark.propTypes = propTypes;
ProductBookmark.defaultProps = defaultProps;

export default ProductBookmark;
